// chat-bubbles.tsx — chat message bubble components shared between the Playground chat UI
// and the Conversations transcript viewer (spec D3/M5 task-2: transcript bubbles reuse
// Playground's bubble rendering — extracted here so both consume the same components
// instead of two copies drifting apart). Extracted verbatim from
// `apps/dashboard/src/pages/Playground.tsx` — no visual or behavior change there.
import { AssistantMarkdown } from '@repo/ui/components/assistant-markdown';
import { StatusChip } from '@repo/ui/components/status-chip';
import { TypingIndicator } from '@repo/ui/components/typing-indicator';
import { useT } from '../locales/context';

export interface ChatCitation {
  url: string;
  title?: string;
}

/** De-dupes citations by `url`, first occurrence wins — a chunk can cite the same source
 * more than once across a single answer. Not exported — `AssistantBubble` (the only
 * consumer) applies it internally, keeping this a component-only module for Fast Refresh. */
function dedupeCitations(citations: ChatCitation[]): ChatCitation[] {
  const seen = new Set<string>();
  const out: ChatCitation[] = [];
  for (const citation of citations) {
    if (seen.has(citation.url)) continue;
    seen.add(citation.url);
    out.push(citation);
  }
  return out;
}

export function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[80%] rounded-2xl rounded-br-[6px] bg-primary px-4 py-2 text-sm text-primary-foreground">
        {text}
      </div>
    </div>
  );
}

/** Playground-only — SSE `error` frames render as their own bubble shape. Not part of a
 * persisted transcript (Conversations never has an "error" message role). */
export function ErrorBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[80%] rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
        {text}
      </div>
    </div>
  );
}

export function AssistantBubble({
  text,
  citations,
  unanswered,
  leadCaptured,
  streaming,
  statusText,
}: {
  text: string;
  citations: ChatCitation[];
  unanswered?: boolean;
  /** Playground-product-demo spec (2026-08-22) — the turn captured a lead (Task 1's engine
   * `lead_captured` event, forwarded as an SSE `lead` frame). Can be true alongside
   * `unanswered` on the same turn — a refusal that escalated to a lead is exactly the case
   * the escalation note produces; rendered after `unanswered` in the same chip row. */
  leadCaptured?: boolean;
  streaming?: boolean;
  /** Rotating-status spec (2026-08-22) — set while `streaming` is true and no text has arrived
   * yet, to a sentence `Playground.tsx` already picked at random from `SEARCHING_STATUS` for
   * the bot's language. Cleared the instant the first token lands, same moment `text` stops
   * being empty, so this and the bare `TypingIndicator` fallback below are mutually exclusive
   * in practice — `statusText` only ever matters while `text` is still `''`. */
  statusText?: string;
}) {
  const unique = dedupeCitations(citations);
  // The dashboard's own locale catalog, not the bot's answer language: these two `StatusChip`
  // labels are chrome the DASHBOARD draws around a turn (whether it went unanswered, whether
  // it captured a lead), not part of the bot's own generated text — same distinction that
  // keeps `Playground.tsx`'s simulated widget bubbles on the bot's language (Task 11's scope
  // fence) while this component's meta-labels follow the owner's dashboard language. Falls
  // back to English with no `LocaleProvider` mounted (`useT`'s documented default), which is
  // exactly what `chat-bubbles.test.tsx` renders against.
  const t = useT();
  return (
    <div className="flex justify-start">
      <div className="flex max-w-[80%] flex-col gap-2">
        {/* Markdown via the same parser the widget and public playground use (2026-08-22 —
            this bubble used to print raw `**bold**`). `AssistantMarkdown` owns paragraph/
            line-break layout, so no `whitespace-pre-wrap` here. */}
        <div className="rounded-2xl rounded-bl-[6px] bg-muted px-4 py-2 text-sm">
          {text ? (
            <AssistantMarkdown text={text} />
          ) : streaming ? (
            <TypingIndicator sentence={statusText} />
          ) : (
            ''
          )}
          {(unanswered || leadCaptured) && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {unanswered && <StatusChip state="paused" label={t.conversations.unansweredChipLabel} />}
              {leadCaptured && <StatusChip state="ready" label={t.conversations.leadCapturedTag} />}
            </div>
          )}
        </div>
        {unique.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {unique.map((citation) => (
              <a
                key={citation.url}
                href={citation.url}
                target="_blank"
                rel="noreferrer"
                title={citation.url}
                className="rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                {citation.title || citation.url}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
